<script lang="ts">
import { MRZ_GENERATOR_EN } from "$lib/i18n/copy/mrz-generator";
import { generateTd3, type Sex } from "$lib/seo/mrz";
import {
  CHECK_DIGIT_GAP,
  CHECK_DIGIT_STEPS,
  COMPOSITE_DIGIT,
  EXPLAINER_LINKS,
  GENERATOR_FAQ,
  MRZ_DEFINITION,
  WORKED_EXAMPLE,
  WORKED_EXAMPLE_LEAD,
} from "$lib/seo/mrz-explainer";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";

const title = "MRZ generator – build a valid synthetic passport MRZ (TD3)";
const description =
  "Generate a valid ICAO 9303 TD3 MRZ (machine-readable zone) with correct check digits from synthetic inputs. Client-side; for testing, never real personal data.";

// The product's one invented holder, the same person the recognition fixture
// and every documentation example carry. Not a real person.
let surname = $state("PARADEIGMA");
let givenNames = $state("ELENI SOFIA");
let issuingCountry = $state("GRC");
let nationality = $state("GRC");
let documentNumber = $state("AM7304518");
let dateOfBirth = $state("940308");
let expiryDate = $state("320310");
let personalNumber = $state("PN48291630");
let sex = $state<Sex>("F");

const lines = $derived(
  generateTd3({
    documentType: "P",
    issuingCountry,
    surname,
    givenNames,
    documentNumber,
    nationality,
    dateOfBirth,
    sex,
    expiryDate,
    personalNumber,
  }),
);

const mrzText = $derived(`${lines.line1}\n${lines.line2}`);

let copied = $state(false);

async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(mrzText);
    copied = true;
    setTimeout(() => {
      copied = false;
    }, 1500);
  } catch {
    // Clipboard access can be denied; the MRZ is still selectable by hand.
  }
}
</script>

<SeoHead {title} {description} path="/mrz-generator" />

<div>
  <main class="mx-auto max-w-3xl px-6 py-10">
    <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl">MRZ generator</h1>
    <p class="mt-4 max-w-2xl text-lg text-slate-600">
      Build a valid passport machine-readable zone (ICAO 9303 TD3) with correct check digits. Use it
      to create test fixtures – the output is synthetic and must never carry real personal data.
    </p>
    <p class="mt-4 max-w-2xl text-slate-600" data-testid="checker-callout">
      {MRZ_GENERATOR_EN.checkerCallout.before}
      <a href="/mrz-checker" class="font-medium text-slate-900 underline hover:text-slate-600"
        >{MRZ_GENERATOR_EN.checkerCallout.anchor}</a
      >{MRZ_GENERATOR_EN.checkerCallout.after}
    </p>

    <form class="mt-8 grid gap-4 sm:grid-cols-2">
      <div>
        <label for="surname" class="block text-sm font-medium text-slate-700">Surname</label>
        <input id="surname" bind:value={surname} class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="given" class="block text-sm font-medium text-slate-700">Given names</label>
        <input id="given" bind:value={givenNames} class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="issuing" class="block text-sm font-medium text-slate-700">Issuing state (alpha-3)</label>
        <input id="issuing" bind:value={issuingCountry} maxlength="3" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="nationality" class="block text-sm font-medium text-slate-700">Nationality (alpha-3)</label>
        <input id="nationality" bind:value={nationality} maxlength="3" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="docnum" class="block text-sm font-medium text-slate-700">Document number</label>
        <input id="docnum" bind:value={documentNumber} maxlength="9" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="personal" class="block text-sm font-medium text-slate-700">Personal number (optional)</label>
        <input id="personal" bind:value={personalNumber} maxlength="14" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="dob" class="block text-sm font-medium text-slate-700">Date of birth (YYMMDD)</label>
        <input id="dob" bind:value={dateOfBirth} maxlength="6" inputmode="numeric" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="expiry" class="block text-sm font-medium text-slate-700">Expiry date (YYMMDD)</label>
        <input id="expiry" bind:value={expiryDate} maxlength="6" inputmode="numeric" class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-slate-500 focus:outline-none" />
      </div>
      <div>
        <label for="sex" class="block text-sm font-medium text-slate-700">Sex</label>
        <select id="sex" bind:value={sex} class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none">
          <option value="M">Male</option>
          <option value="F">Female</option>
          <option value="X">Unspecified</option>
        </select>
      </div>
    </form>

    <section class="mt-8" aria-labelledby="output">
      <div class="flex items-center justify-between">
        <h2 id="output" class="text-lg font-semibold text-slate-900">Generated MRZ</h2>
        <button type="button" onclick={copy} class="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre class="mt-3 scroll-x rounded-lg bg-slate-900 p-4 text-sm leading-relaxed text-slate-100" aria-live="polite"><code>{mrzText}</code></pre>
      <p class="mt-3 text-sm text-slate-500">
        Verify it with the
        <a href="/mrz-checker" class="underline hover:text-slate-800">MRZ parser</a>.
      </p>
    </section>

    <!-- The explainer below the tool: somebody who came to generate a zone gets
         the form first, and somebody who came to understand the arithmetic
         reaches it with one scroll. -->
    <section class="mt-16" aria-labelledby="what-is-an-mrz">
      <h2 id="what-is-an-mrz" class="text-2xl font-semibold tracking-tight">
        What a machine-readable zone is
      </h2>
      <p class="mt-4 text-slate-600">{MRZ_DEFINITION}</p>
    </section>

    <section class="mt-12" aria-labelledby="check-digit">
      <h2 id="check-digit" class="text-2xl font-semibold tracking-tight">
        How the check digits above are calculated
      </h2>
      <ol class="mt-4 list-decimal space-y-2 pl-6 text-slate-600">
        {#each CHECK_DIGIT_STEPS as step (step)}
          <li>{step}</li>
        {/each}
      </ol>

      <p class="mt-6 text-slate-600">{WORKED_EXAMPLE_LEAD}</p>
      <div class="mt-4 overflow-x-auto">
        <table class="w-full border-collapse text-sm">
          <caption class="sr-only">
            The 7-3-1 weighting worked through, character by character, on a specimen document
            number
          </caption>
          <thead>
            <tr class="text-left text-slate-500">
              <th scope="col" class="py-2 pr-4 font-medium">Character</th>
              <th scope="col" class="py-2 pr-4 font-medium">Value</th>
              <th scope="col" class="py-2 pr-4 font-medium">Weight</th>
              <th scope="col" class="py-2 font-medium">Product</th>
            </tr>
          </thead>
          <tbody>
            {#each WORKED_EXAMPLE.steps as step, index (index)}
              <tr class="border-t border-slate-200">
                <td class="py-2 pr-4 font-mono text-slate-900">{step.char}</td>
                <td class="py-2 pr-4 text-slate-700">{step.value}</td>
                <td class="py-2 pr-4 text-slate-700">{step.weight}</td>
                <td class="py-2 text-slate-700">{step.product}</td>
              </tr>
            {/each}
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-slate-300">
              <th scope="row" colspan="3" class="py-2 pr-4 text-left font-medium text-slate-700">
                Sum, then modulo 10
              </th>
              <td class="py-2 font-medium text-slate-900">
                {WORKED_EXAMPLE.sum} → {WORKED_EXAMPLE.digit}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p class="mt-4 text-slate-600">
        Enter <code class="font-mono text-sm">{WORKED_EXAMPLE.field}</code> as the document number
        above and the digit after it in the generated zone is
        <code class="font-mono text-sm font-semibold text-slate-900">{WORKED_EXAMPLE.digit}</code> –
        the same arithmetic, run by the same code.
      </p>

      <h3 class="mt-8 text-lg font-semibold text-slate-900">The composite digit</h3>
      <p class="mt-2 text-slate-600">{COMPOSITE_DIGIT}</p>

      <h3 class="mt-8 text-lg font-semibold text-slate-900">What the digits do not cover</h3>
      <p class="mt-2 text-slate-600">{CHECK_DIGIT_GAP}</p>
    </section>

    <section class="mt-12" aria-labelledby="faq">
      <h2 id="faq" class="text-2xl font-semibold tracking-tight">Questions people ask</h2>
      <div class="mt-5 divide-y divide-slate-200 border-y border-slate-200">
        {#each GENERATOR_FAQ as entry (entry.id)}
          <details class="group py-4">
            <summary
              class="cursor-pointer list-none text-base font-medium text-slate-900 marker:content-none"
            >
              <h3 class="inline">{entry.question}</h3>
              <span aria-hidden="true" class="ml-2 text-slate-400 group-open:hidden">+</span>
              <span aria-hidden="true" class="ml-2 hidden text-slate-400 group-open:inline">−</span>
            </summary>
            <p class="mt-3 text-slate-600">{entry.answer}</p>
          </details>
        {/each}
      </div>
    </section>

    <section class="mt-12" aria-labelledby="next">
      <h2 id="next" class="text-2xl font-semibold tracking-tight">Where to go next</h2>
      <ul class="mt-5 space-y-3 text-slate-600">
        <li>
          <a href="/mrz-checker" class="font-medium text-slate-900 underline hover:text-slate-600"
            >MRZ parser</a
          > – paste a zone and watch every check digit recomputed.
        </li>
        {#each EXPLAINER_LINKS as link (link.href)}
          <li>
            <a href={link.href} class="font-medium text-slate-900 underline hover:text-slate-600"
              >{link.label}</a
            > – {link.blurb}
          </li>
        {/each}
      </ul>
    </section>
  </main>

  <SiteFooter />
</div>
